import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, ArrowDownIcon, GlobeIcon } from 'lucide-react';
import { Countdown } from './Countdown';
const particles = [{
  top: '15%',
  left: '10%',
  delay: 0,
  duration: 3
}, {
  top: '65%',
  left: '15%',
  delay: 1,
  duration: 3.5
}, {
  top: '45%',
  left: '50%',
  delay: 2,
  duration: 3
}];
export function HeroSection() {
  return <section className="relative min-h-[85vh] md:min-h-screen flex items-center pt-20 pb-10 md:pt-24 md:pb-16 overflow-hidden" data-id="element-225">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.15)_0%,transparent_60%)]" data-id="element-226"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-50" data-id="element-227"></div>

      {/* Electric Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-228">
        {particles.map((particle, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 bg-brand-glow rounded-full shadow-[0_0_15px_rgba(77,159,255,1)]" style={{
        top: particle.top,
        left: particle.left
      }} animate={{
        y: [0, -60, 0],
        opacity: [0, 1, 0],
        scale: [0, 1.5, 0]
      }} transition={{
        duration: particle.duration,
        repeat: Infinity,
        delay: particle.delay,
        ease: 'easeInOut'
      }} data-id="element-229" />)}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-230">
        <div className="text-center max-w-4xl mx-auto" data-id="element-231">
          {/* Trending Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 md:py-2.5 rounded-full glass-card border-brand-cyan/50 shadow-[0_0_20px_rgba(0,102,255,0.3)] mb-5 md:mb-8" data-id="element-232">
            <span className="relative flex h-3 w-3" data-id="element-233">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-glow opacity-75" data-id="element-234"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-cyan" data-id="element-235"></span>
            </span>
            <span className="text-sm font-bold text-white tracking-wide" data-id="element-236">
              #1 TRENDING — WORLD CUP 2026
            </span>
          </motion.div>

          {/* Tagline */}
          <motion.p initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="text-sm sm:text-xl md:text-2xl text-brand-glow mb-3 md:mb-5 tracking-widest uppercase font-bold electric-glow" data-id="element-237">
            Don't Just Watch History — Wear It
          </motion.p>

          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl sm:text-5xl md:text-7xl font-heading font-black tracking-tight mb-2 md:mb-4 leading-tight" data-id="element-238">
            One Tap.{' '}
            <span className="text-gradient drop-shadow-[0_0_40px_rgba(0,102,255,0.5)]" data-id="element-239">
              Infinite Possibilities.
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base md:text-xl text-gray-300 mb-6 md:mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-240">
            The world's first smart t-shirt built for the World Cup experience.
            Tap it with any phone to instantly share your team, match stats,
            social links, and exclusive fan content — no app needed.{' '}
            <span className="font-bold text-white uppercase" data-id="element-241">
              100% WATERPROOF.
            </span>{' '}
            Built for game days, travel, and everyday wear. Updated in real
            time.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-6 md:mb-16" data-id="element-242">
            <a href="#pricing" className="w-full sm:w-auto bg-brand-cyan hover:bg-brand-glow text-white font-bold text-lg py-4 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_35px_rgba(0,102,255,0.8)] flex items-center justify-center gap-2 hover:-translate-y-1" data-id="element-243">
              <ZapIcon className="w-5 h-5 fill-current" data-id="element-244" />
              Order Now — Limited Edition
            </a>
            <a href="#nfc-animation" className="w-full sm:w-auto glass-card hover:bg-white/10 text-white font-medium text-lg py-4 px-8 rounded-full transition-all flex items-center justify-center gap-2" data-id="element-245">
              See How It Works <ArrowDownIcon className="w-5 h-5" data-id="element-246" />
            </a>
          </motion.div>

          {/* Flash Bundle Promo */}
          <motion.a href="#pricing" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 md:mb-12 cursor-pointer group transition-all hover:-translate-y-0.5" style={{
          background: 'linear-gradient(135deg, rgba(250,204,21,0.1) 0%, rgba(0,102,255,0.1) 100%)',
          border: '1px solid rgba(250,204,21,0.25)'
        }} data-id="element-247">
            <span className="text-yellow-400 text-sm" data-id="element-248">🔥</span>
            <span className="text-sm font-bold text-yellow-400 group-hover:text-yellow-300 transition-colors" data-id="element-249">
              Flash Bundles — Save up to 25%
            </span>
            <span className="text-yellow-400/60 text-xs" data-id="element-250">→</span>
          </motion.a>

          {/* World Cup Event Section */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="relative" data-id="element-251">
            <div className="absolute inset-0 bg-brand-cyan/10 blur-[100px] rounded-full" data-id="element-252"></div>

            <div className="flex items-center justify-center gap-1.5 md:gap-2 mb-4 md:mb-6" data-id="element-253">
              <GlobeIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-glow" data-id="element-254" />
              <p className="text-[10px] md:text-sm font-bold text-brand-glow uppercase tracking-widest" data-id="element-255">
                FIFA World Cup 2026 — June 11, Mexico City
              </p>
              <GlobeIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-glow" data-id="element-256" />
            </div>

            <Countdown data-id="element-257" />

            {/* Social Proof */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1,
            duration: 1
          }} className="mt-4 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm font-medium text-gray-300" data-id="element-258">
              <p className="flex items-center gap-2" data-id="element-259">
                <span className="relative flex h-2 w-2" data-id="element-260">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-261"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-262"></span>
                </span>
                <strong className="text-white" data-id="element-263">47,000+ fans</strong> already
                secured theirs
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}